import { useCallback, useEffect, useRef, useState } from 'react';

const STREAK_KEY = 'sl_audio_mode_answer_streak_v1';
const NOTICES_KEY = 'sl_audio_mode_notices_shown_v1';
const FIRST_NOTICE_AT = 2;
const SECOND_NOTICE_AT = 5;
const MAX_NOTICES = 2;

const readInt = (key: string): number => {
  try {
    const raw = typeof window !== 'undefined' ? window.localStorage.getItem(key) : null;
    const parsed = raw ? Number.parseInt(raw, 10) : 0;
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
  } catch {
    return 0;
  }
};

const writeInt = (key: string, value: number): void => {
  try {
    window.localStorage.setItem(key, String(Math.max(0, value)));
  } catch {}
};

export function useAudioModeReminder(isListeningMode: boolean) {
  const [showReminder, setShowReminder] = useState(false);
  const [reminderStep, setReminderStep] = useState<1 | 2>(1);
  const streakRef = useRef<number>(readInt(STREAK_KEY));
  const noticesRef = useRef<number>(readInt(NOTICES_KEY));
  const isListeningModeRef = useRef(isListeningMode);
  const previousModeRef = useRef(isListeningMode);

  useEffect(() => {
    isListeningModeRef.current = isListeningMode;

    if (previousModeRef.current && !isListeningMode) {
      streakRef.current = 0;
      writeInt(STREAK_KEY, 0);
      setShowReminder(false);
    }

    previousModeRef.current = isListeningMode;
  }, [isListeningMode]);

  const dismissReminder = useCallback(() => {
    setShowReminder(false);
  }, []);

  const recordAnswer = useCallback((wasAudioModeAnswer: boolean, canDisplayReminder: boolean) => {
    if (!wasAudioModeAnswer) {
      streakRef.current = 0;
      writeInt(STREAK_KEY, 0);
      return;
    }

    if (!isListeningModeRef.current) {
      return;
    }

    streakRef.current += 1;
    writeInt(STREAK_KEY, streakRef.current);

    if (noticesRef.current >= MAX_NOTICES || !canDisplayReminder) {
      return;
    }

    if (streakRef.current >= FIRST_NOTICE_AT && noticesRef.current === 0) {
      noticesRef.current = 1;
      writeInt(NOTICES_KEY, 1);
      setReminderStep(1);
      setShowReminder(true);
      return;
    }

    if (streakRef.current >= SECOND_NOTICE_AT && noticesRef.current === 1) {
      noticesRef.current = 2;
      writeInt(NOTICES_KEY, 2);
      setReminderStep(2);
      setShowReminder(true);
    }
  }, []);

  return {
    dismissReminder,
    recordAnswer,
    reminderStep,
    showReminder,
  };
}
